import { useState, useCallback, useMemo } from "react";

import { DollarSign, Check, Trash2, ExternalLink } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { cn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { SearchTextLink } from "../SearchTextLink";
import { ExtractedBudget } from "../types";

interface BudgetExtractionResultsProps {
  budgets: ExtractedBudget[];
}

interface BudgetCardState {
  dismissed: boolean;
  opened: boolean;
}

/**
 * Maps extracted budget keys (from the AI API) to TaskBudget model field names.
 * The API returns snake_case keys; the model uses camelCase field names.
 */
const BUDGET_FIELD_MAP: Record<string, string> = {
  contract_amount: "contractAmount",
  change_orders: "changeOrders",
  labor_estimated: "laborEstimated",
  labor_actual: "laborActual",
  materials_estimated: "materialsEstimated",
  materials_actual: "materialsActual",
  subs_estimated: "subsEstimated",
  subs_actual: "subsActual",
  rentals_estimated: "rentalsEstimated",
  rentals_actual: "rentalsActual",
  miscellaneous_estimated: "miscellaneousEstimated",
  miscellaneous_actual: "miscellaneousActual",
  days_estimated: "daysEstimated",
  description: "description",
};

/** Keys to exclude from the display card (internal / non-displayable). */
const HIDDEN_KEYS = new Set(["search_text", "description"]);

/** Preferred display order — matches document-AI budget payload key order. */
const FIELD_ORDER = [
  "budget_id",
  "contract_amount",
  "change_orders",
  "total_contract",
  "ytd_income",
  "total_cost_estimated",
  "total_cost_actual",
  "total_cost_variance",
  "ytd_costs",
  "gross_profit_estimated",
  "gross_margin_estimated",
  "gross_profit_actual",
  "gross_margin_actual",
  "gross_profit_variance",
  "gross_margin_variance",
  "percentage_billed",
  "balance_left_to_bill",
  "received_dollars",
  "labor_estimated",
  "labor_actual",
  "labor_variance",
  "outstanding_ar",
  "materials_estimated",
  "materials_actual",
  "materials_variance",
  "subs_estimated",
  "subs_actual",
  "subs_variance",
  "rentals_estimated",
  "rentals_actual",
  "rentals_variance",
  "miscellaneous_estimated",
  "miscellaneous_actual",
  "miscellaneous_variance",
  "days_estimated",
  "days_actual",
  "days_variance",
];

const formatLabel = (key: string): string => {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const isBlank = (value: unknown): boolean => {
  if (value == null) return true;
  const text = String(value).trim();
  return text === "" || text.toLowerCase() === "null" || text.toLowerCase() === "undefined";
};

const isLongText = (value: string): boolean =>
  value.length > 42 || value.includes("\n") || /\s{2,}/.test(value);

/** Format numeric-looking money/margin values for readout without changing stored data. */
const formatDisplayValue = (key: string, value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  // Keep ids and free text as-is
  if (key === "budget_id" || key.includes("description")) return trimmed;

  const normalized = trimmed.replace(/[$,%\s,]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return trimmed;

  const num = Number(normalized);
  if (!Number.isFinite(num)) return trimmed;

  if (key.includes("margin") || key.includes("percentage") || trimmed.includes("%")) {
    return `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }

  return num.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const getSortedEntries = (budget: ExtractedBudget): [string, string][] => {
  const entries = Object.entries(budget).filter(
    ([key, value]) => !HIDDEN_KEYS.has(key) && !isBlank(value)
  ) as [string, string][];

  return entries.sort(([a], [b]) => {
    const ai = FIELD_ORDER.indexOf(a);
    const bi = FIELD_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};

export const BudgetExtractionResults = ({ budgets }: BudgetExtractionResultsProps) => {
  const { project, entityRecord, editorHandler } = useFileDetailModal();

  const [cardStates, setCardStates] = useState<Record<number, BudgetCardState>>(() => {
    const initial: Record<number, BudgetCardState> = {};
    budgets.forEach((_, index) => {
      initial[index] = { dismissed: false, opened: false };
    });
    return initial;
  });

  const updateCardState = useCallback((index: number, update: Partial<BudgetCardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...update },
    }));
  }, []);

  const handleOpenBudgetEditor = useCallback((budget: ExtractedBudget, index: number) => {
    if (!project || !editorHandler) return;

    const taskBudgetStore = project.taskBudgetStore;
    if (!taskBudgetStore) return;

    const budgetData: Record<string, any> = {};
    for (const [apiKey, value] of Object.entries(budget)) {
      if (apiKey === "search_text") continue;
      const modelField = BUDGET_FIELD_MAP[apiKey];
      if (modelField) {
        if (value == null) {
          budgetData[modelField] = modelField === "description" ? "" : 0;
        } else {
          const numVal = parseFloat(value);
          budgetData[modelField] = isNaN(numVal) ? value : numVal;
        }
      }
    }

    const newRecord = taskBudgetStore.createRecord(budgetData);
    editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, {
      budget: newRecord,
      onBudgetCreated: (budgetId: number | string) => {
        if (entityRecord) {
          (entityRecord as any).budgetId = budgetId;
        }
      },
    });
    updateCardState(index, { opened: true });
  }, [project, editorHandler, updateCardState, entityRecord]);

  const handleDismiss = useCallback((index: number) => {
    updateCardState(index, { dismissed: true });
  }, [updateCardState]);

  const visibleCount = useMemo(
    () => budgets.filter((_, index) => !cardStates[index]?.dismissed).length,
    [budgets, cardStates]
  );

  if (visibleCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-12">
        <p className="text-sm text-muted-foreground">All budget cards have been dismissed.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      <AnimatePresence initial={false}>
        {budgets.map((budget, index) => {
          const state = cardStates[index];
          if (state?.dismissed) return null;

          const description = budget.description?.trim();
          const entries = getSortedEntries(budget);

          return (
            <motion.div
              key={index}
              initial={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className={cn(
                "overflow-hidden rounded-swivu-base border border-border bg-card",
                state?.opened && "border-primary/25 bg-primary/[0.03]"
              )}
            >
              <div className="flex flex-col gap-3 p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <DollarSign className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium text-foreground">
                      Budget{budgets.length > 1 ? ` #${index + 1}` : ""}
                    </span>
                  </div>

                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      title={state?.opened ? "Open in Budget Editor again" : "Open in Budget Editor"}
                      onClick={() => handleOpenBudgetEditor(budget, index)}
                      className={cn(
                        "relative flex h-7 w-7 items-center justify-center rounded-md transition-all duration-150",
                        "hover:bg-primary/10 hover:text-primary active:bg-primary/20",
                        state?.opened && "text-primary"
                      )}
                    >
                      {state?.opened ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <ExternalLink className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      title="Dismiss"
                      onClick={() => handleDismiss(index)}
                      className="relative flex h-7 w-7 items-center justify-center rounded-md transition-all duration-150 hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {description ? (
                  <div className="rounded-md bg-muted/40 px-3 py-2.5">
                    <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Description
                    </p>
                    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
                      {description}
                    </p>
                  </div>
                ) : null}

                {entries.length > 0 ? (
                  <dl className="flex flex-col gap-2.5">
                    {entries.map(([key, value]) => {
                      const display = formatDisplayValue(key, value);
                      const stacked = isLongText(display);

                      return (
                        <div
                          key={key}
                          className={cn(
                            "gap-1 text-sm",
                            stacked
                              ? "flex flex-col"
                              : "grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-baseline gap-x-3"
                          )}
                        >
                          <dt className="text-muted-foreground">{formatLabel(key)}</dt>
                          <dd
                            className={cn(
                              "font-medium text-foreground",
                              stacked
                                ? "whitespace-pre-wrap break-words"
                                : "text-right tabular-nums break-words"
                            )}
                          >
                            {display}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                ) : !description ? (
                  <p className="text-sm text-muted-foreground">No budget fields extracted.</p>
                ) : null}

                {budget.search_text ? (
                  <div className="pt-0.5">
                    <SearchTextLink searchText={budget.search_text} />
                  </div>
                ) : null}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
